import { AgentRegistryIcon } from '@/components/icons/agent-registry-icon'
import { ChatHistoryIcon } from '@/components/icons/chat-history-icon'
import { DataCatalogIcon } from '@/components/icons/data-catalog-icon'
import { PlusIcon } from '@/components/icons/plus-icon'
import { ScheduleIcon } from '@/components/icons/schedule-icon'
import { ServiceRegistryIcon } from '@/components/icons/service-registry-icon'
import { Button } from '@/components/ui/button'
import { useAllWorkflowsQuery, Workflow } from '@/hooks/api/use-all-workflows-query'
import React from 'react'
import { useSplitView } from '../../split-view-context'
import { createStaticTab, createWorkflowTab } from '../factories'
import { getTabMetadata } from '../metadata'
import { isChatContextTab, TabTypeStatic } from '../types'

export const BlankTabContent = ({ tabId }: Readonly<{ tabId: string }>) => {
  const { addTab, filterTabs, getTabPositionMetadata } = useSplitView()
  const { data: chatHistoryItems } = useAllWorkflowsQuery({
    queryOptions: { select: (d) => d.data.slice(0, 3) },
  })
  const tabPosition = React.useMemo(
    () => getTabPositionMetadata({ tabId }),
    [getTabPositionMetadata, tabId],
  )

  const handleTabCardClick = (tabType: TabTypeStatic) => {
    addTab({
      tab: createStaticTab(tabType),
      index: tabPosition?.index,
      panelId: tabPosition?.panelId,
    })
    filterTabs({ tabType: TabTypeStatic.Blank })
  }

  const handleChatHistoryItemClick = (w: Workflow) => {
    addTab({
      index: tabPosition?.index,
      panelId: tabPosition?.panelId,
      tab: createWorkflowTab({ workflowId: w.workflowId, title: w.title }),
    })
    filterTabs({
      tabType: TabTypeStatic.Blank,
      predicate: (t) => isChatContextTab(t) && t.workflowId !== w.workflowId,
    })
  }

  return (
    <div className='my-auto flex flex-col items-center gap-4'>
      <h1 className='font-neue-machina text-2xl font-semibold'>What would you like to do?</h1>
      {chatHistoryItems && chatHistoryItems.length > 0 && (
        <>
          <p className='text-xs font-light tracking-wide text-neutral-500'>
            Jump right back into a previous Chat
          </p>
          <div className='flex w-full flex-col gap-1 text-sm'>
            {chatHistoryItems.map((w) => (
              <React.Fragment key={w.workflowId}>
                <Button
                  unstyled
                  className='w-full rounded-lg p-4 text-left hover:bg-neutral-50'
                  onClick={() => handleChatHistoryItemClick(w)}
                >
                  {w.title}
                </Button>
                <div className='h-px w-full bg-neutral-75' />
              </React.Fragment>
            ))}
          </div>
        </>
      )}
      <Button
        variant='black'
        shape='square'
        size='lg'
        className='rounded-lg text-base'
        onClick={() => handleTabCardClick(TabTypeStatic.NewChat)}
      >
        <PlusIcon className='size-3 shrink-0 text-mango-300' />
        Start a New Chat
      </Button>
      <p className='mt-4 text-xs font-light tracking-wide text-neutral-500'>
        Quickly access your pages
      </p>
      <div className='mx-auto flex flex-wrap items-center justify-center gap-4'>
        {TAB_TYPE_CARDS_CONFIG.map(({ type, Icon }) => {
          const { title, description } = getTabMetadata(type)
          return (
            <Button
              key={type}
              unstyled
              className='group/tab-card flex w-44 flex-col items-center rounded-lg px-4 py-3 text-center text-neutral-500 outline-mango-300 hover:bg-neutral-50 focus:bg-neutral-50'
              onClick={() => handleTabCardClick(type)}
              title={`Navigate to ${title}`}
            >
              <Icon className='size-12 shrink-0 text-neutral-150 group-hover/tab-card:text-mango-300 group-focus/tab-card:text-mango-300 [&_path]:fill-neutral-150 group-hover/tab-card:[&_path]:fill-mango-300 group-focus/tab-card:[&_path]:fill-mango-300' />
              <span className='pb-1.5 pt-2 font-neue-machina text-base'>{title}</span>
              <span className='text-sm font-normal'>{description}</span>
            </Button>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// TAB TYPE CARDS CONFIG
// =============================================================================

const TAB_TYPE_CARDS_CONFIG = [
  {
    type: TabTypeStatic.History,
    Icon: ChatHistoryIcon,
  },
  {
    type: TabTypeStatic.AgentRegistry,
    Icon: AgentRegistryIcon,
  },
  {
    type: TabTypeStatic.ServiceRegistry,
    Icon: ServiceRegistryIcon,
  },
  {
    type: TabTypeStatic.DataCatalog,
    Icon: DataCatalogIcon,
  },
  {
    type: TabTypeStatic.Schedule,
    Icon: ScheduleIcon,
  },
] as const
